import { ITEMS, RARITY_ORDER } from "../content/items";
import { LOOT_TABLES } from "../content/world";
import { randomInt, stream, weightedPick } from "../game/rng";
import type { GameState, InventoryStack, LootTableDefinition, Rarity } from "../game/types";

const clone = <T,>(value: T): T => structuredClone(value);

function addStack(state: GameState, stack: InventoryStack): void {
  const definition = ITEMS[stack.itemId];
  if (!definition) return;
  if (definition.stack > 1 && !stack.rarity && stack.quality === undefined) {
    const existing = state.inventory.find((entry) => entry.itemId === stack.itemId && !entry.rarity && entry.quality === undefined);
    if (existing) { existing.quantity += stack.quantity; return; }
  }
  state.inventory.push(stack);
}

export function rollRarity(profile: LootTableDefinition["rarityProfile"], luck: number, rng: () => number): Rarity {
  const adjusted = RARITY_ORDER.map((rarity, index) => {
    const base = profile[rarity] ?? 0;
    const transfer = luck * .045 * index;
    return { item: rarity, weight: Math.max(0, base * (1 + transfer) * (index === 0 ? Math.max(.35, 1 - luck * .02) : 1)) };
  });
  return weightedPick(adjusted, rng);
}

export interface LootResult { state: GameState; drops: InventoryStack[]; rarityRolls: Rarity[]; pityTriggered: boolean }

export function rollLoot(input: GameState, tableId: string, sourceKey: string): LootResult {
  const state = clone(input);
  const table = LOOT_TABLES[tableId];
  if (!table) return { state, drops: [], rarityRolls: [], pityTriggered: false };
  const rng = stream(state.seed, `loot-${sourceKey}-${state.rngCounter++}`);
  const pityKey = table.pityGroup ?? table.id;
  const currentPity = state.lootPity[pityKey] ?? 0;
  const pityTriggered = currentPity >= 17;
  const drops: InventoryStack[] = [];
  const rarityRolls: Rarity[] = [];

  for (let roll = 0; roll < table.rolls; roll += 1) {
    let rarity = rollRarity(table.rarityProfile, state.stats.luck + (state.classId === "shadeweaver" ? 4 : 0), rng);
    if (pityTriggered && RARITY_ORDER.indexOf(rarity) < RARITY_ORDER.indexOf("rare")) rarity = "rare";
    rarityRolls.push(rarity);
    const rarityIndex = RARITY_ORDER.indexOf(rarity);
    let candidates = table.entries.filter((entry) => !entry.rarity || RARITY_ORDER.indexOf(entry.rarity) <= rarityIndex + 1);
    candidates = candidates.filter((entry) => !entry.unique || !state.uniqueDrops.includes(entry.itemId));
    if (!candidates.length) candidates = table.entries.filter((entry) => !entry.unique);
    if (!candidates.length) continue;
    const entry = weightedPick(candidates.map((candidate) => ({ item: candidate, weight: candidate.weight })), rng);
    const amount = randomInt(entry.min, entry.max, rng);
    const stack: InventoryStack = {
      itemId: entry.itemId,
      quantity: amount,
      rarity: entry.rarity && ITEMS[entry.itemId]?.stack === 1 ? entry.rarity : undefined,
      durability: ITEMS[entry.itemId]?.stack === 1 ? 100 : undefined,
      uid: ITEMS[entry.itemId]?.stack === 1 ? `loot-${state.seed}-${state.rngCounter++}` : undefined,
    };
    addStack(state, stack);
    drops.push(stack);
    if (entry.unique) state.uniqueDrops.push(entry.itemId);
  }

  const rareOrBetter = rarityRolls.some((rarity) => RARITY_ORDER.indexOf(rarity) >= RARITY_ORDER.indexOf("rare"));
  state.lootPity[pityKey] = rareOrBetter ? 0 : currentPity + 1;
  if (pityTriggered) state.lootPity[pityKey] = 0;
  return { state, drops, rarityRolls, pityTriggered };
}
